import { apiRequest } from "../http";
import type {
  PropertyType,
  SelectOption,
  PropertyDefinition,
  ListSchema,
  ViewConfig,
  SmartFillConfig as DbSmartFillConfig,
  NoteProperties,
} from "db/types";
import type {
  WorkflowStatus,
  WorkflowMetadata,
  WorkflowConfig,
} from "./workflow";

export type {
  PropertyType,
  SelectOption,
  PropertyDefinition,
  ViewConfig,
  NoteProperties,
  WorkflowStatus,
  WorkflowMetadata,
  WorkflowConfig,
};

export type NoteListSchema = ListSchema;

export type SmartFillConfig = DbSmartFillConfig;

export type NoteListKind = "FOLDER" | "LIST" | "PROJECT";

export type NoteListChild = {
  id: string;
  name: string;
  icon?: string | null;
};

export type NoteList = {
  id: string;
  userId: string;
  name: string;
  description?: string | null;
  icon?: string | null;
  kind?: NoteListKind;
  parentId?: string | null;
  sortOrder?: number | null;
  schema: NoteListSchema | null;
  viewConfig?: ViewConfig | null;
  smartFillEnabled?: boolean;
  smartFillConfig?: SmartFillConfig | null;
  workflowEnabled?: boolean;
  workflowConfig?: WorkflowConfig | null;
  workflowPrompt?: string | null;
  createdAt: string;
  updatedAt: string;
  children?: NoteListChild[];
  _count?: {
    notes: number;
    children?: number;
  };
};

export type ParsingStatus =
  | "PENDING"
  | "FETCHING"
  | "FETCH_FAILED"
  | "PARSING"
  | "PARSING_FAILED"
  | "PARTIAL"
  | "COMPLETED"
  | "NEEDS_REVIEW";

export type ParserType = "tmdb" | "youtube" | "generic";

export type ParserCandidate = {
  id: string;
  externalId: string;
  title: string;
  metadata: {
    releaseYear?: number | string;
    mediaType?: string;
    [key: string]: unknown;
  };
  confidence: number;
};

export type ParseAttempt = {
  id: string;
  noteListNoteId: string;
  sourceUrl: string;
  status: ParsingStatus;
  parserUsed: ParserType | null;
  extractedData: {
    candidates?: ParserCandidate[];
    selectedCandidateId?: string;
  } | null;
  errorMessage: string | null;
  attemptNumber: number;
  createdAt: string;
};

export type NoteListNote = {
  id: string;
  noteListId: string;
  noteId: string;
  properties: Record<string, unknown>;
  sortOrder: number | null;
  smartFillStatus?: string | null;
  smartFillError?: string | null;
  smartFillMetadata?: SmartFillMetadata | null;
  sourceUrl?: string | null;
  parsingStatus?: ParsingStatus | null;
  parserUsed?: ParserType | null;
  parseConfidence?: number | null;
  parseError?: string | null;
  workflowStatus?: WorkflowStatus | null;
  workflowError?: string | null;
  workflowMetadata?: WorkflowMetadata | null;
  userModifiedFields?: string[] | null;
  createdAt: string;
  note: {
    id: string;
    title: string | null;
    content: string;
    createdAt: string;
    updatedAt: string;
  };
  // Latest parse attempt with candidates for disambiguation
  parseAttempts?: Array<{
    id: string;
    status: ParsingStatus;
    extractedData: {
      candidates?: ParserCandidate[];
      selectedCandidateId?: string;
    } | null;
    parserUsed: ParserType | null;
  }>;
};

export type NoteListWithNotes = NoteList & {
  notes: NoteListNote[];
};

export const fetchNoteLists = ({
  signal,
  request,
}: {
  signal?: AbortSignal;
  request?: Request;
} = {}) => {
  return apiRequest<NoteList[]>("/api/note-lists", {
    signal,
    request,
  });
};

export const fetchNoteList = (
  id: string,
  {
    signal,
    request,
  }: {
    signal?: AbortSignal;
    request?: Request;
  } = {},
) => {
  return apiRequest<NoteListWithNotes>(`/api/note-lists/${id}`, {
    signal,
    request,
  });
};

export const createNoteList = (data: {
  name: string;
  description?: string;
  icon?: string;
  kind?: NoteListKind;
  schema?: NoteListSchema;
  viewConfig?: ViewConfig;
  smartFillEnabled?: boolean;
  smartFillConfig?: SmartFillConfig;
  parentId?: string | null;
}) => {
  return apiRequest<NoteList>("/api/note-lists", {
    method: "POST",
    body: data,
  });
};

export const updateNoteList = (
  id: string,
  data: {
    name?: string;
    description?: string | null;
    kind?: NoteListKind;
    schema?: NoteListSchema;
    viewConfig?: ViewConfig | null;
    smartFillEnabled?: boolean;
    smartFillConfig?: SmartFillConfig;
    parentId?: string | null;
  },
) => {
  return apiRequest<NoteList>(`/api/note-lists/${id}`, {
    method: "PATCH",
    body: data,
  });
};

export const deleteNoteList = (id: string, options?: { cascade?: boolean }) => {
  const params = options?.cascade ? "?cascade=true" : "";
  return apiRequest<void>(`/api/note-lists/${id}${params}`, {
    method: "DELETE",
  });
};

export const reorderNoteList = (
  id: string,
  data: { newSortOrder: number; newParentId?: string | null },
) => {
  return apiRequest<NoteList>(`/api/note-lists/${id}/reorder`, {
    method: "POST",
    body: data,
  });
};

export const addNoteToList = (
  listId: string,
  data: {
    noteId: string;
    properties?: Record<string, unknown>;
    sourceUrl?: string;
  },
) => {
  return apiRequest<NoteListNote>(`/api/note-lists/${listId}/notes`, {
    method: "POST",
    body: data,
  });
};

export const updateNoteInList = (
  listId: string,
  noteId: string,
  data: { properties?: Record<string, unknown>; status?: string | null },
) => {
  return apiRequest<NoteListNote>(`/api/note-lists/${listId}/notes/${noteId}`, {
    method: "PATCH",
    body: data,
  });
};

export const removeNoteFromList = (listId: string, noteId: string) => {
  return apiRequest<void>(`/api/note-lists/${listId}/notes/${noteId}`, {
    method: "DELETE",
  });
};

export const reorderNoteInList = (
  listId: string,
  noteId: string,
  data: { sortOrder: number },
) => {
  return apiRequest<NoteListNote>(
    `/api/note-lists/${listId}/notes/${noteId}/reorder`,
    {
      method: "POST",
      body: { newSortOrder: data.sortOrder },
    },
  );
};

export const updateListSchema = (
  listId: string,
  data: {
    addProperty?: { name: string; definition: PropertyDefinition };
    renameProperty?: { oldName: string; newName: string };
    removeProperty?: { name: string };
    reorderProperties?: { orderedKeys: string[] };
  },
) => {
  let body: Record<string, unknown>;

  if (data.addProperty) {
    body = {
      action: "add",
      propertyKey: data.addProperty.name,
      definition: data.addProperty.definition,
    };
  } else if (data.renameProperty) {
    body = {
      action: "rename",
      propertyKey: data.renameProperty.oldName,
      newKey: data.renameProperty.newName,
    };
  } else if (data.removeProperty) {
    body = {
      action: "remove",
      propertyKey: data.removeProperty.name,
    };
  } else if (data.reorderProperties) {
    body = {
      action: "reorder",
      orderedKeys: data.reorderProperties.orderedKeys,
    };
  } else {
    throw new Error("Invalid updateListSchema call: no action specified");
  }

  return apiRequest<NoteList>(`/api/note-lists/${listId}/schema`, {
    method: "PATCH",
    body,
  });
};

export type SmartFillStatus =
  | "PENDING"
  | "EXTRACTING"
  | "COMPLETED"
  | "PARTIAL"
  | "NEEDS_REVIEW"
  | "FAILED";

export type UncertainValue = {
  value: unknown;
  confidence: number;
  alternatives?: unknown[];
  reason?: string;
};

export type SmartFillMetadata = {
  extractedAt: string;
  confidence: number;
  uncertainFields: Record<string, UncertainValue>;
  sourceUrl?: string;
  dataSource?: string;
  rawData?: Record<string, unknown>;
};

export type SmartFillResult = {
  success: boolean;
  status: SmartFillStatus;
  extractedValues: Record<string, unknown>;
  uncertainFields: Record<string, UncertainValue>;
  error?: string;
};

export type SmartFillStatusResponse = {
  status: SmartFillStatus | null;
  metadata: SmartFillMetadata | null;
  properties: Record<string, unknown>;
  sourceUrl?: string;
};

export const triggerSmartFill = (
  listId: string,
  noteId: string,
  data?: { sourceUrl?: string; forceReextract?: boolean },
) => {
  return apiRequest<SmartFillResult>(
    `/api/note-lists/${listId}/notes/${noteId}/smart-fill`,
    {
      method: "POST",
      body: data || {},
    },
  );
};

export const getSmartFillStatus = (listId: string, noteId: string) => {
  return apiRequest<SmartFillStatusResponse>(
    `/api/note-lists/${listId}/notes/${noteId}/smart-fill/status`,
  );
};

export const confirmSmartFillValue = (
  listId: string,
  noteId: string,
  data: { fieldKey: string; confirmed: boolean; newValue?: unknown },
) => {
  return apiRequest<{ success: boolean; error?: string }>(
    `/api/note-lists/${listId}/notes/${noteId}/smart-fill/confirm`,
    {
      method: "POST",
      body: data,
    },
  );
};

export const updateSmartFillConfig = (
  listId: string,
  data: {
    smartFillEnabled?: boolean;
    smartFillConfig?: SmartFillConfig;
  },
) => {
  return apiRequest<NoteList>(`/api/note-lists/${listId}/smart-fill-config`, {
    method: "PATCH",
    body: data,
  });
};

export type ParseNoteUrlResult = {
  success: boolean;
  result?: {
    status: ParsingStatus;
    properties: Record<string, unknown> | null;
    candidateCount: number;
    parserUsed: ParserType | null;
    confidence: number | null;
    error?: string;
  };
  noteListNote?: {
    id: string;
    parsingStatus: ParsingStatus | null;
    parserUsed: ParserType | null;
    parseConfidence: number | null;
    properties: Record<string, unknown>;
  };
  parseAttempt?: ParseAttempt;
  error?: string;
};

export const parseNoteUrl = (
  noteListNoteId: string,
  data?: { url?: string; forceRefresh?: boolean },
) => {
  return apiRequest<ParseNoteUrlResult>(
    `/api/note-list-notes/${noteListNoteId}/parse`,
    {
      method: "POST",
      body: data || {},
    },
  );
};

export const retryParseNoteUrl = (noteListNoteId: string) => {
  return apiRequest<ParseNoteUrlResult>(
    `/api/note-list-notes/${noteListNoteId}/retry-parse`,
    {
      method: "POST",
    },
  );
};

export type SelectCandidateResult = {
  success: boolean;
  noteListNote?: {
    id: string;
    parsingStatus: ParsingStatus;
    properties: Record<string, unknown>;
  };
  error?: string;
};

export const selectParseCandidate = (
  listId: string,
  noteListNoteId: string,
  data: { candidateId: string; parseAttemptId: string },
) => {
  return apiRequest<SelectCandidateResult>(
    `/api/note-lists/${listId}/notes/${noteListNoteId}/select-candidate`,
    {
      method: "POST",
      body: data,
    },
  );
};

export const getParseAttempts = (noteListNoteId: string) => {
  return apiRequest<ParseAttempt[]>(
    `/api/note-list-notes/${noteListNoteId}/parse-attempts`,
  );
};
